import type { Logger } from "@expert/logging";

import { HTTPError } from "ky";

import { getHashedValue, removeCountryCode } from "@/shared-utils";

import type {
    BillingAccountNumberGetCustomerDetailsParams,
    CustomerDetailsPayload,
    CustomerDetailsRequestBody,
    GetCustomerDetailsParams,
    PhoneNumberGetCustomerDetailsParams,
} from "./types";

import { getCustomerDetailsApi } from "./api";

export const getCustomerDetails = async (
    logger: Logger,
    params: GetCustomerDetailsParams,
): Promise<CustomerDetailsPayload | null> => {
    const childLogger = logger.child({ module: "customerDetails" });
    const { modelResultsMdn, partner, sessionId, searchType, dispatcher, customerDetailsCallback } = params;
    let jsonPayload: CustomerDetailsRequestBody;
    const formattedMdn = modelResultsMdn && removeCountryCode(modelResultsMdn);
    const hashedMdn = formattedMdn && (await getHashedValue(formattedMdn));

    if (isValidPhoneNumberSearchType(params)) {
        jsonPayload = {
            partner,
            searchType,
            sessionId,
            criteria: {
                phoneNumber: removeCountryCode(params.mdn),
            },
            hashedMdn,
        };
    } else if (isValidBillingAccountNumberAndPinSearchType(params)) {
        jsonPayload = {
            partner,
            searchType,
            criteria: {
                billingAccountNumber: params.billingAccountNumber,
                customerVerificationMethod: params.pin ? "PIN" : "BANV",
                pin: params.pin,
            },
            hashedMdn,
        };
    } else {
        return null;
    }

    try {
        const client = getCustomerDetailsApi();

        const apiPath = "v3/customer/details";
        const response = await client.post(apiPath, { json: jsonPayload, retry: 0 }).json<CustomerDetailsPayload>();

        // only update the MDN if it was provided in the initial request
        if (response.primaryAccountHolder?.mdn && isValidPhoneNumberSearchType(params)) {
            response.primaryAccountHolder.mdn = params.mdn;
        }
        void dispatcher?.dispatchBusinessEvent("CustomerDetailsStats", {
            includesPrimaryAccountHolder: !!response.primaryAccountHolder,
            includesAccountNumber: !!response.primaryAccountHolder?.accountNumber,
            includesCustomerAddress: !!response.primaryAccountHolder?.address,
            includesDeviceReplacementDetails: !!response.deviceActivation,
            includesPreviousSummary: !!response.previousSummary,
            previousDeviceCount: response.previousDevices?.length ?? 0,
        });

        customerDetailsCallback?.(response);

        return response;
    } catch (error: unknown) {
        if (error instanceof HTTPError) {
            childLogger.warn(
                { error, status: error.response.status },
                "getCustomerDetails API call failed with status",
            );
            return null;
        }
        childLogger.warn({ error }, "getCustomerDetails API call failed with exception");
        return null;
    }
};

function isValidPhoneNumberSearchType(params: GetCustomerDetailsParams): params is PhoneNumberGetCustomerDetailsParams {
    return params.searchType === "phoneNumber" && params.mdn != null;
}

function isValidBillingAccountNumberAndPinSearchType(
    params: GetCustomerDetailsParams,
): params is BillingAccountNumberGetCustomerDetailsParams {
    return params.searchType === "billingAccountNumberAndPin" && params.billingAccountNumber != null;
}
